import { useEffect, useRef, useState } from 'react'
import { useAuth } from '../auth'
import { useI18n } from '../i18n'
import { supabase } from '../lib/supabase'
import { avvFehlerText, landName } from '../lib/avv'
import { AvvRpcError, avvAnnehmen, avvPartnerLaden, avvVorschau, type AvvPartner, type AvvVorschau } from '../data/supabaseAdapter'
import { Button, Card } from '../ui/kit'
import AvvSchritt, { gepruefteAvvAnnahme } from './AvvSchritt'

const empty: AvvPartner = { firma: '', strasse: '', plz: '', ort: '', land: '' }
export function AvvBestandsAnnahme({ gate, onDone }: { gate: boolean; onDone?: () => void }) {
  const { tenantId, session, rolle, avvNeuLaden, avvGateAussetzen } = useAuth()
  const { lang } = useI18n()
  const de = lang === 'de'
  const [partner, setPartner] = useState<AvvPartner>(empty)
  const [country, setCountry] = useState('')
  const [other, setOther] = useState('')
  const [preview, setPreview] = useState<AvvVorschau | null>(null)
  const [checked, setChecked] = useState(false)
  const [pending, setPending] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [seeded, setSeeded] = useState(false)
  const [reload, setReload] = useState(0)
  const revision = useRef(0)
  const context = `${session?.user.id}:${tenantId}:${rolle}`
  const current = useRef(context)
  current.current = context
  const unavailable = () => {
    setPreview(null)
    setError(de ? 'Der Vertrag ist gerade nicht verfügbar. Bitte versuchen Sie es später erneut.' : 'The agreement is currently unavailable. Please try again later.')
    if (gate) avvGateAussetzen()
  }
  useEffect(() => {
    let active = true
    current.current = context
    revision.current++
    setPartner(empty); setCountry(''); setOther(''); setChecked(false); setPreview(null); setSeeded(false)
    if (!supabase || !tenantId || !session || rolle !== 'owner') { setPending(false); return }
    void avvPartnerLaden(supabase, tenantId).then(p => {
      if (!active) return
      setPartner(p); setSeeded(true)
    }).catch(() => { if (active) unavailable() })
    return () => { active = false; revision.current++; if (current.current === context) current.current = '' }
  }, [context])
  const updatePartner = (p: AvvPartner) => {
    revision.current++; setChecked(false); setPending(true); setError(null); setPartner(p)
  }
  useEffect(() => {
    if (!seeded || !supabase || !tenantId || rolle !== 'owner') return
    let active = true
    const token = revision.current
    setPending(true); setChecked(false)
    void avvVorschau(supabase, partner, tenantId).then(v => {
      if (!active || token !== revision.current) return
      if (!v || (v.fehlend.length === 0 && (!v.text?.trim() || !v.sha256))) unavailable()
      else { setPreview(v); setError(null) }
    }).catch(err => {
      if (!active || token !== revision.current) return
      if (err instanceof AvvRpcError && ['avv_vertragspartner_unvollstaendig', 'avv_vertragspartner_zu_lang', 'avv_vertragspartner_zeichen', 'avv_platzhalter_offen'].includes(err.kennung)) {
        // Missing/invalid partner fields are editable states, not gate failures.
        setPreview(v => v ? { ...v, text: null, sha256: null } : { kennung: '', titel: de ? 'Auftragsverarbeitung' : 'Data processing agreement', sprache: 'de', gilt_ab: '', fehlend: [], text: null, sha256: null, uebersetzung_en: null })
        setError(avvFehlerText(err.kennung, err.hint, lang))
      } else unavailable()
    }).finally(() => { if (active && token === revision.current) setPending(false) })
    return () => { active = false }
  }, [partner, seeded, reload, context, lang])
  if (rolle !== 'owner' || !session || !tenantId) return null
  const submit = async () => {
    if (pending || busy) return
    const token = revision.current, scope = context
    setBusy(true); setError(null)
    try {
      const a = await gepruefteAvvAnnahme(preview, partner, checked)
      if (!a || token !== revision.current || current.current !== scope || !supabase) return
      await avvAnnehmen(supabase, tenantId, a)
      if (current.current !== scope) return
      await avvNeuLaden()
      if (current.current === scope) onDone?.()
    } catch (err) {
      if (current.current !== scope) return
      const code = err instanceof AvvRpcError ? err.kennung : err instanceof Error && err.message === 'avv_hash_abweichend' ? 'avv_hash_abweichend' : null
      setError(avvFehlerText(code, err instanceof AvvRpcError ? err.hint : null, lang))
      setChecked(false); revision.current++; setReload(n => n + 1)
    } finally { if (current.current === scope) setBusy(false) }
  }
  return <div className="space-y-3">
    <AvvSchritt preview={preview} partner={partner} countryCode={country} otherCountry={other} checked={checked} pending={pending} de={de} error={error}
      onPartner={updatePartner}
      onCountry={c => { setCountry(c); updatePartner({ ...partner, land: landName(c, other) }) }}
      onOtherCountry={v => { setOther(v); updatePartner({ ...partner, land: landName('OTHER', v) }) }}
      onChecked={v => { revision.current++; setChecked(v) }} />
    <Button type="button" variant="primary" disabled={pending || busy || !checked || !preview?.text || !preview.sha256 || !!preview.fehlend.length} onClick={() => void submit()}>
      {de ? 'Vertrag annehmen' : 'Accept agreement'}
    </Button>
  </div>
}
export default function AvvGate() {
  const { signOut, tenantId, session } = useAuth()
  const { lang } = useI18n()
  return <div className="min-h-dvh p-4 flex justify-center items-center"><Card className="w-full max-w-xl p-4 space-y-3">
    <AvvBestandsAnnahme key={`${session?.user.id}:${tenantId}`} gate />
    <Button type="button" variant="ghost" onClick={() => void signOut()}>{lang === 'de' ? 'Abmelden' : 'Sign out'}</Button>
    <a className="text-sm underline" href="/konto-loeschen">{lang === 'de' ? 'Konto und Daten löschen' : 'Delete account and data'}</a>
  </Card></div>
}
